import React, { useEffect, useMemo, useState } from 'react';
import { renderFullscreenDocument } from './DocumentsFolderPage';
import { formatMelApplicability } from '../utils/melApplicability';

const normalizeAtaChapter = value => {
  const raw = String(value || '').trim();
  const withoutPrefix = raw.replace(/^ATA[\s-]*/i, '');
  const numberMatch = withoutPrefix.match(/^(\d{1,2})/);
  return numberMatch ? numberMatch[1].padStart(2, '0') : withoutPrefix;
};

const deduplicateEntries = entries => {
  const seen = new Set();
  return entries.filter(entry => {
    const key = entry.entry_id || `${entry.operator_code || ''}|${entry.ata_chapter || ''}|${entry.title || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const ComplaintMelEntryBrowser = ({
  ataChapter,
  ataDescription,
  entries,
  loading,
  selectedEntryId,
  onSelect
}) => {
  const [search, setSearch] = useState('');
  const [previewEntryId, setPreviewEntryId] = useState('');
  const normalizedAtaChapter = normalizeAtaChapter(ataChapter);

  const chapterEntries = useMemo(() => deduplicateEntries(
    (entries || [])
      .filter(entry => normalizeAtaChapter(entry.ata_chapter) === normalizedAtaChapter)
      .sort((left, right) => String(left.title || '').localeCompare(String(right.title || ''), undefined, { numeric: true }))
  ), [entries, normalizedAtaChapter]);

  const visibleEntries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return chapterEntries;
    return chapterEntries.filter(entry => (
      `${entry.title || ''} ${entry.alert_type || ''} ${entry.search_text || ''}`
        .toLowerCase()
        .includes(query)
    ));
  }, [chapterEntries, search]);

  const previewEntry = chapterEntries.find(entry => entry.entry_id === previewEntryId)
    || chapterEntries.find(entry => entry.entry_id === selectedEntryId)
    || null;

  useEffect(() => {
    setPreviewEntryId('');
    setSearch('');
  }, [ataChapter]);

  return (
    <div className="complaint-mel-browser">
      <section className="complaint-mel-index-panel">
        <div className="complaint-mel-browser-search-row">
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search MEL Entries..."
            aria-label="Search MEL Entries"
          />
        </div>
        <div className="complaint-mel-browser-head">
          <div>
            <span>ATA {normalizedAtaChapter} - {ataDescription || 'MEL Entries'}</span>
            <small>{loading ? 'Loading MEL Entries...' : `${visibleEntries.length} entries`}</small>
          </div>
        </div>
        <div className="complaint-mel-browser-list">
          {loading ? (
            <div className="complaint-mel-browser-empty">Loading MEL Entries...</div>
          ) : !visibleEntries.length ? (
            <div className="complaint-mel-browser-empty">
              {chapterEntries.length ? 'No MEL Entries match your search.' : `No MEL Entries found for ATA ${normalizedAtaChapter}.`}
            </div>
          ) : visibleEntries.map((entry, index) => (
            <button
              key={entry.entry_id || `${entry.title}-${index}`}
              type="button"
              className={`complaint-mel-browser-item ${previewEntry === entry ? 'active' : ''}`}
              onClick={() => setPreviewEntryId(entry.entry_id)}
            >
              <span className="complaint-mel-browser-ref">ATA {normalizeAtaChapter(entry.ata_chapter)}</span>
              <span className="complaint-mel-browser-title">{entry.title}</span>
              <span className="complaint-mel-browser-meta">
                <span>{entry.applicability_types || entry.applicability || 'ALL'}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="complaint-mel-preview-panel">
        {previewEntry ? (
          <>
            <div className="complaint-mel-preview-toolbar">
              <div>
                <span className="complaint-mel-preview-kicker">MEL Entry</span>
                <strong>ATA {normalizeAtaChapter(previewEntry.ata_chapter)}</strong>
              </div>
              <button type="button" onClick={() => onSelect(previewEntry)}>Select Entry</button>
            </div>
            <div className="complaint-mel-preview-content complaint-mel-entry-preview">
              {renderFullscreenDocument(previewEntry, 'entries', formatMelApplicability(previewEntry))}
            </div>
          </>
        ) : (
          <div className="complaint-mel-browser-empty complaint-mel-preview-empty">
            Select an MEL Entry to review its dispatch conditions.
          </div>
        )}
      </section>
    </div>
  );
};

export default ComplaintMelEntryBrowser;
